import React, { useEffect } from "react";

import * as Sentry from "@sentry/browser";
// eslint-disable-next-line no-restricted-imports
import { useFlags } from "launchdarkly-react-client-sdk";
import * as Redux from "react-redux";
import { Route } from "react-router-dom";
import { Routes } from "react-router-dom";

import { ErrorToast } from "./components/ErrorToast/ErrorToast";
import { AppProviders } from "./context";
import { useContactUs } from "./context/contact-us-context";
import { MerchantLinks } from "./domain/Link/MerchantLinks";
import { ContactUs } from "./domain/Onboarding/ContactUs";
import { Faqs } from "./domain/Onboarding/Faqs/Faqs";
import { RegisteredCountry } from "./domain/Onboarding/RegisteredCountry";
import { useRestore } from "./hooks";
import { amplitudeEventProperties } from "./lib/amplitude/metrics";
import { Login } from "./pages/Login";
import { lazyWithRetry } from "./utils/lazy-with-retry";
const DevTools = lazyWithRetry(() => import("./dev-tools"), "DevTools");
const CheckEmail = lazyWithRetry(
  () => import("./pages/CheckEmail"),
  "CheckEmail"
);
const Lead = lazyWithRetry(() => import("./pages/Lead"), "Lead");
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), "NotFound");
const Unauthorized = lazyWithRetry(
  () => import("./pages/Unauthorized"),
  "Unauthorized"
);
const Onboarding = lazyWithRetry(
  () => import("./pages/Onboarding"),
  "Onboarding"
);
const OnboardingStatus = lazyWithRetry(
  () => import("./pages/OnboardingStatus"),
  "OnboardingStatus"
);
const AlternativePlatform = lazyWithRetry(
  () => import("./pages/AlternativePlatform"),
  "AlternativePlatform"
);
const BlockOnboarding = lazyWithRetry(
  () => import("./pages/BlockOnboarding"),
  "BlockOnboarding"
);
const WrongAccount = lazyWithRetry(
  () => import("./pages/WrongAccount"),
  "WrongAccount"
);
const Square = lazyWithRetry(() => import("./pages/Square"), "Square");
const CashPay = lazyWithRetry(() => import("./pages/CashPay"), "CashPay");
const Maintenance = lazyWithRetry(
  () => import("./pages/Maintenance"),
  "Maintenance"
);

export class App extends React.Component {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    Sentry.withScope((scope: Sentry.Scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key as keyof React.ErrorInfo]);
      });
      Sentry.captureException(error);
    });
  }

  render(): JSX.Element {
    return <Providers />;
  }
}

export const AppRoutes: React.FC = () => (
  <Routes>
    {/**
     * TODO: Consider typing route paths
     */}
    <Route path="/" element={<Login />} />
    <Route path="/lead" element={<Lead />} />
    <Route path="/check-email" element={<CheckEmail />} />
    <Route path="/onboarding/*" element={<Onboarding />} />
    <Route path="/status" element={<OnboardingStatus />} />
    <Route path="/cash" element={<CashPay />} />
    <Route path="/square" element={<Square />} />
    <Route
      path="/squarespace"
      element={<AlternativePlatform platform="SQUARESPACE" />}
    />
    <Route
      path="/maropost"
      element={<AlternativePlatform platform="MAROPOST" />}
    />
    <Route
      path="/thank-you"
      element={<BlockOnboarding category={"thankYou"} />}
    />
    <Route
      path="/invalid-country"
      element={<BlockOnboarding category={"invalidCountry"} />}
    />
    <Route
      path="/agency"
      element={<BlockOnboarding category={"thankYouAgency"} />}
    />
    <Route path="/maintenance" element={<Maintenance />} />
    <Route path="/wrong-account" element={<WrongAccount />} />
    <Route path="/401" element={<Unauthorized />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export const Providers: React.FC = () => {
  const ldFeatureFlags = useFlags();

  amplitudeEventProperties.set(ldFeatureFlags);
  return (
    <React.Suspense fallback={<div />}>
      <AppProviders>
        <AppRoutes />
        <ContactUsAndFaq />
        <RegisteredCountry />
        <MerchantLinks />
        <ErrorToast />
        <DevTools />
      </AppProviders>
    </React.Suspense>
  );
};

export const ContactUsAndFaq: React.FC = () => {
  const [faqOpen, setFaqOpen] = React.useState(false);
  const { showContactUs } = useContactUs();
  const oppOwnerEmail = Redux.useSelector(
    ({ merchant }: any) => merchant.merchantAccount.opportunityOwnerEmail
  );
  const { refetchRestore } = useRestore(false);

  useEffect(() => {
    // we want to refetch to see if sf heroku connect has synced with an oppOwnerEmail on the prospect when faq get's opened
    if (faqOpen && !oppOwnerEmail && !showContactUs) {
      refetchRestore();
    }
  }, [faqOpen, oppOwnerEmail, showContactUs]);

  return (
    <>
      <ContactUs />
      <Faqs
        setFaqOpen={setFaqOpen}
        faqOpen={faqOpen}
        showContactUs={showContactUs}
      />
    </>
  );
};
